import { DockviewApi } from 'dockview';
import { useAtomValue, useSetAtom } from 'jotai';
import { moveTabHistoryCursorAtom, tabHistoriesAtomFamily } from '@/entities/tab/model/tab.atom';
import { useCallback, useMemo } from 'react';
import { getTabTitle } from '@/shared/config/route-core';

export function useForwardTab(tabId: string, api: DockviewApi | null) {
  const tabHistories = useAtomValue(tabHistoriesAtomFamily(tabId));
  const moveTabHistoryCursor = useSetAtom(moveTabHistoryCursorAtom);

  const targetPanel = useMemo(() => {
    if (!api) return undefined;
    return api.panels.find((panel) => panel.id === tabId);
  }, [api, tabId]);

  const canGoForward = useMemo(() => {
    return tabHistories.cursor < tabHistories.histories.length - 1;
  }, [tabHistories.cursor, tabHistories.histories.length]);

  const forward = useCallback(() => {
    if (!targetPanel) return;

    const { cursor, histories } = tabHistories;
    if (histories.length === 0) return;

    const nextCursor = Math.min(cursor + 1, histories.length - 1);
    if (nextCursor === cursor) return;

    const history = histories[nextCursor];
    if (!history) {
      console.error(`History not found at index: ${nextCursor}`);
      return;
    }

    moveTabHistoryCursor({ tabId, direction: 'forward' });

    targetPanel.update({ params: history.context });
    targetPanel.setTitle(getTabTitle(history.context));
    targetPanel.focus();
  }, [moveTabHistoryCursor, tabId, tabHistories, targetPanel]);

  return { forward, canGoForward };
}
